import { get as _get } from 'lodash';
import { utc } from 'moment';

type DisplayValueFn<T> = (value: T) => string | number;
type SortDirection = 'asc' | 'desc' | '';
type ValueComparatorFn = (a: number | string, b: number | string) => number;
type ComparatorFn = (a: number | string, b: number | string) => number;
type ValueFormatterFn = (value: string | number) => string | number;

export class TableSortUtil {
  private _valueAccessor: Record<string, DisplayValueFn<unknown>> = {};

  private _comparator: Record<string, ComparatorFn> = {};

  private readonly DEFAULT_ROW_VALUE: string = '...';

  sortData<T>(unsortedData: T[], properties: { direction: SortDirection; active: DisplayValueFn<T> | string }): T[] {
    if (!unsortedData || unsortedData.length === 0 || !properties || !properties.direction || !properties.active) {
      return unsortedData || [];
    }
    const { active, direction } = properties;
    const activeAsString = active.toString();
    const valueAccessor: DisplayValueFn<T> = this.getValueAccessor(activeAsString, active);
    const isAsc: boolean = direction === 'asc';
    const comparator: ComparatorFn = this.getComparator(activeAsString, unsortedData, valueAccessor, isAsc);
    return unsortedData.sort((a: T, b: T) => {
      const propertyA: string | number = valueAccessor(a);
      const propertyB: string | number = valueAccessor(b);
      return comparator(propertyA, propertyB);
    });
  }

  private getValueAccessor<T>(
    propertiesActiveAsString: string,
    propertiesActive: DisplayValueFn<T> | string,
  ): DisplayValueFn<T> {
    if (!this._valueAccessor[propertiesActiveAsString]) {
      this._valueAccessor[propertiesActiveAsString] = this._getValueAccessor(propertiesActive);
    }
    return this._valueAccessor[propertiesActiveAsString];
  }

  private _getValueAccessor<T>(propertiesActive: DisplayValueFn<T> | string): DisplayValueFn<T> {
    if (!propertiesActive) {
      return (value: T): string | number => value as unknown as string | number;
    }
    if (typeof propertiesActive === 'function') {
      return propertiesActive;
    }
    return (value: T): string | number => _get(value, propertiesActive);
  }

  private getComparator<T>(
    propertiesActiveAsString: string,
    data: T[],
    valueAccessor: DisplayValueFn<T>,
    isAsc: boolean,
  ): ComparatorFn {
    const propertiesActiveWithIsAscIdentifier = propertiesActiveAsString + isAsc;
    let foundComparator = this._comparator[propertiesActiveWithIsAscIdentifier];
    if (!foundComparator) {
      const testValue: string | number = this.getTestValue(data, valueAccessor);
      foundComparator = this._getComparator(testValue, isAsc);

      if (this.isValidTestValue(testValue)) {
        this._comparator[propertiesActiveWithIsAscIdentifier] = foundComparator;
      }
    }
    return foundComparator;
  }

  private _getComparator<T>(testValue: string | number, isAsc: boolean): ComparatorFn | null {
    if (typeof testValue === 'number') {
      return this.composeComparator(this.simpleValueEmitter, this.numberComparator, isAsc);
    }
    if (!testValue) {
      return this.composeComparator(this.simpleValueEmitter, this.isEqualsComparator, isAsc);
    }
    if (this.isStringInteger(testValue)) {
      return this.composeComparator(this.parseStringIntegerToNumber, this.numberComparator, isAsc);
    }
    if (this.isStringDecimal(testValue)) {
      return this.composeComparator(this.parseStringDecimalToNumber, this.numberComparator, isAsc);
    }
    if (this.isDate(testValue)) {
      return this.composeComparator(this.parseDateStringToNumber, this.numberComparator, isAsc);
    }
    return this.composeComparator(this.stringFormatter, this.stringComparator, isAsc);
  }

  private composeComparator(
    formatterFn: ValueFormatterFn,
    comparatorFn: ValueComparatorFn,
    isAsc: boolean,
  ): ComparatorFn {
    const directionMultiplier: number = isAsc ? 1 : -1;
    return (a: string | number, b: string | number): number => {
      return comparatorFn(formatterFn(a), formatterFn(b)) * directionMultiplier;
    };
  }

  private getTestValue<T>(data: T[], valueAccessor: DisplayValueFn<T>): string | number | undefined {
    let testValue: string | number;
    let index: number = 0;
    while (index < data.length && !this.isValidTestValue(testValue)) {
      testValue = valueAccessor(data[index]);
      index++;
    }
    return testValue;
  }

  private isValidTestValue(testValue: string | number): boolean {
    return (testValue && testValue !== this.DEFAULT_ROW_VALUE) || Number.isFinite(testValue);
  }

  /** ** Value comparator * */

  private numberComparator(a: number, b: number): number {
    if (Number.isNaN(a) || a < b) {
      return -1;
    }
    return a === b ? 0 : 1;
  }

  private stringComparator(a: string, b: string): number {
    return a.localeCompare(b);
  }

  private isEqualsComparator(a: string, b: string): number {
    return 0;
  }

  /** ** Value type checker * */

  private isDate(potentialDate: string | number): boolean {
    if (typeof potentialDate !== 'string') {
      return false;
    }
    const found = potentialDate.match(/(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.((?:19|20)\d{2}|\d{2})/);
    return found && found.length > 0;
  }

  private isStringDecimal(value: string): boolean {
    const found = value.match(/^\d+\.\d+$/);
    return found && found.length > 0;
  }

  private isStringInteger(value: string): boolean {
    const found = value.match(/^0$|(^-?[1-9]\d*$)/);
    return found && found.length > 0;
  }

  /** ** Value parser/formatter * */

  private parseDateStringToNumber(dateString: string): number {
    return utc(dateString, 'DD.MM.YYYY HH:mm:ss').unix();
  }

  private parseStringIntegerToNumber(value: string): number {
    return Number.parseInt(value, 10);
  }

  private parseStringDecimalToNumber(value: string): number {
    return Number.parseFloat(value);
  }

  private stringFormatter(value: string): string {
    return (value || '').toLocaleLowerCase();
  }

  private simpleValueEmitter(value: string | number): string | number {
    return value;
  }
}
